import type { ConsumableSearchResponse } from '../consumable';
import type { MedpraxMedicationResponse } from '../external-integrations/medprax';

import type {
  Medication,
  MedicationMeasure,
  MedicationRouteCode,
} from './schema';

const getMedicationDisplay = (medication: MedpraxMedicationResponse) => {
  if (medication.FullDescription) {
    return medication.FullDescription;
  }

  if (medication.StrengthMetric1) {
    return `${medication.Name} ${medication.StrengthMetric1} ${medication.UnitOfMeasure1}`;
  }

  if (medication.VolumeMetric2) {
    return `${medication.Name} ${medication.VolumeMetric2} ${medication.UnitOfMeasure2}`;
  }

  if (medication.VolumeMetric3) {
    return `${medication.Name} ${medication.VolumeMetric3} ${medication.UnitOfMeasure3}`;
  }

  return medication.Name;
};

const createMeasure = (
  unit?: string,
  value?: string,
): MedicationMeasure | undefined => {
  if (!(unit && value)) {
    return undefined;
  }

  return { unit, value };
};

export const mapMedpraxToMedicationModel = (
  med: MedpraxMedicationResponse,
): Medication => {
  return {
    code: med.Code,
    name: med.Name,
    nappiCode: med.NappiCodeFirstSixDigits,
    schedule: med.Schedule,
    dispensingVolumePacksize: med.DispensingVolumePacksize,
    strength: createMeasure(med.UnitOfMeasure1, med.StrengthMetric1),
    volume: createMeasure(med.UnitOfMeasure2, med.VolumeMetric2),
    individualPackSize: createMeasure(med.UnitOfMeasure3, med.VolumeMetric3),
    // PresentationCode is always defined
    route: med.PresentationCode as MedicationRouteCode,
  };
};

export const mapMedpraxConsumableModel = (
  med: MedpraxMedicationResponse,
): ConsumableSearchResponse => ({
  medpraxCode: med.Code,
  name: getMedicationDisplay(med),
  nappiCode: med.NappiCodeFirstSixDigits,
});
